import { useState } from 'react';
import toast from 'react-hot-toast';
import { ImportResult, ImportPreview } from '../types';
import { apiService } from '../services/api';

interface CSVImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

type Step = 'upload' | 'preview' | 'result';

export function CSVImportModal({ isOpen, onClose, onSuccess }: CSVImportModalProps) {
  const [file, setFile] = useState<File | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [step, setStep] = useState<Step>('upload');
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [updateExisting, setUpdateExisting] = useState(false);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);

  if (!isOpen) return null;

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (selectedFile) {
      if (selectedFile.type !== 'text/csv' && !selectedFile.name.endsWith('.csv')) {
        toast.error('Por favor, selecione um arquivo CSV válido');
        return;
      }
      setFile(selectedFile);
      setPreview(null);
      setImportResult(null);
      setStep('upload');
      setUpdateExisting(false);
    }
  };

  const handlePreview = async () => {
    if (!file) {
      toast.error('Selecione um arquivo CSV');
      return;
    }
    setIsLoading(true);
    try {
      const result = await apiService.previewCSV(file);
      setPreview(result);
      setStep('preview');
      if (result.duplicates > 0) {
        setUpdateExisting(false);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erro ao analisar arquivo';
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleImport = async () => {
    if (!file) {
      toast.error('Selecione um arquivo CSV');
      return;
    }
    setIsLoading(true);
    try {
      const result = await apiService.importCSV(file, updateExisting);
      setImportResult(result);
      setStep('result');

      const totalSuccess = result.successfulImports + result.updatedImports;
      if (result.failedImports === 0 && result.skippedDuplicates === 0) {
        toast.success(`Importação concluída! ${totalSuccess} contatos processados.`);
        onSuccess();
      } else if (totalSuccess > 0) {
        toast(`Importação parcial: ${totalSuccess} processados, ${result.failedImports} erros.`, {
          icon: '⚠️',
          style: { border: '1px solid #f59e0b', color: '#92400e' },
        });
        onSuccess();
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erro ao importar arquivo';
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const blob = await apiService.downloadCSVTemplate();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'template-contatos.csv';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Template baixado com sucesso!');
    } catch (error) {
      console.error('Erro ao baixar template:', error);
      toast.error('Erro ao baixar template CSV');
    }
  };

  const handleClose = () => {
    setFile(null);
    setPreview(null);
    setImportResult(null);
    setStep('upload');
    setUpdateExisting(false);
    onClose();
  };

  const handleBack = () => {
    setStep('upload');
    setPreview(null);
    setUpdateExisting(false);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 backdrop-blur-sm overflow-y-auto">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-xl border border-gray-100 my-8" role="dialog" aria-labelledby="import-title">
        <div className="p-6">
          <div className="flex justify-between items-start mb-6">
            <div>
              <h2 id="import-title" className="text-xl font-bold text-gray-900">
                Importar Contatos via CSV
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                {step === 'upload' && 'Importe múltiplos contatos de uma só vez'}
                {step === 'preview' && 'Revise os contatos antes de importar'}
                {step === 'result' && 'Resultado da importação'}
              </p>
            </div>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
              aria-label="Fechar"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="space-y-4">

          {/* ===== STEP 1: UPLOAD ===== */}
          {step === 'upload' && (
            <>
              {/* Template Download */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-medium text-blue-900 text-sm">Template CSV</h3>
                    <p className="text-xs text-blue-700 mt-1">
                      Baixe o template com o formato correto
                    </p>
                  </div>
                  <button
                    onClick={handleDownloadTemplate}
                    className="bg-blue-600 text-white px-3 py-2 rounded-lg hover:bg-blue-700 text-sm font-medium transition-colors flex items-center gap-2"
                    style={{ backgroundColor: 'var(--astra-dark-blue)' }}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Baixar
                  </button>
                </div>
              </div>

              {/* File Upload */}
              <div>
                <label htmlFor="csv-file" className="block text-sm font-medium text-gray-700 mb-2">
                  Selecionar arquivo CSV
                </label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-blue-400 transition-colors">
                  <input
                    id="csv-file"
                    type="file"
                    accept=".csv,text/csv"
                    onChange={handleFileChange}
                    className="hidden"
                  />
                  <label htmlFor="csv-file" className="cursor-pointer">
                    <svg className="w-8 h-8 text-gray-400 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                    <p className="text-sm text-gray-600 font-medium">Clique para selecionar</p>
                    <p className="text-xs text-gray-400 mt-1">Formato: .csv</p>
                  </label>
                </div>
                {file && (
                  <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-center gap-2">
                      <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <p className="text-xs text-green-700 font-medium truncate">
                        {file.name}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Import Instructions */}
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                <h3 className="font-medium text-gray-900 text-sm mb-2">Instruções</h3>
                <ul className="text-xs text-gray-600 space-y-1">
                  <li>• Colunas obrigatórias: <strong>nome</strong> e <strong>telefone</strong></li>
                  <li>• Opcionais: email, cnpj, endereco, observacoes, tags, categoriaId</li>
                  <li>• Aceita separador por vírgula (,) ou ponto e vírgula (;)</li>
                  <li>• Use o template como referência</li>
                </ul>
              </div>
            </>
          )}

          {/* ===== STEP 2: PREVIEW ===== */}
          {step === 'preview' && preview && (
            <>
              {/* Summary */}
              <div className="grid grid-cols-3 gap-2">
                <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-center">
                  <div className="text-xl font-bold text-green-700">{preview.newContacts}</div>
                  <div className="text-xs text-green-600">Novos</div>
                </div>
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-center">
                  <div className="text-xl font-bold text-yellow-700">{preview.duplicates}</div>
                  <div className="text-xs text-yellow-600">Duplicados</div>
                </div>
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-center">
                  <div className="text-xl font-bold text-red-700">{preview.invalid}</div>
                  <div className="text-xs text-red-600">Inválidos</div>
                </div>
              </div>

              {/* Duplicate Contacts List */}
              {preview.duplicates > 0 && (
                <div className="border border-yellow-200 rounded-lg overflow-hidden">
                  <div className="bg-yellow-50 px-4 py-3 border-b border-yellow-200">
                    <h3 className="font-medium text-yellow-900 text-sm">
                      Contatos duplicados encontrados ({preview.duplicates})
                    </h3>
                    <p className="text-xs text-yellow-700 mt-1">
                      Estes contatos já existem no sistema (mesmo telefone)
                    </p>
                  </div>
                  <div className="max-h-48 overflow-y-auto divide-y divide-gray-100">
                    {preview.duplicateContacts.map((dup, index) => (
                      <div key={index} className="px-4 py-3 bg-white hover:bg-gray-50">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-xs font-medium text-gray-400">Linha {dup.row}</span>
                              <span className="text-xs text-yellow-600 bg-yellow-100 px-1.5 py-0.5 rounded">duplicado</span>
                            </div>
                            <p className="text-sm font-medium text-gray-900 mt-1 truncate">
                              {dup.csvData.nome}
                            </p>
                            <p className="text-xs text-gray-500">{dup.csvData.telefone}</p>
                          </div>
                          <div className="text-right flex-shrink-0">
                            <p className="text-xs text-gray-400">Existente:</p>
                            <p className="text-xs font-medium text-gray-700">{dup.existing.nome}</p>
                            {dup.existing.categoria && (
                              <p className="text-xs text-gray-400">{dup.existing.categoria}</p>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Update Toggle */}
                  <div className="bg-yellow-50 px-4 py-3 border-t border-yellow-200">
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={updateExisting}
                        onChange={(e) => setUpdateExisting(e.target.checked)}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <div>
                        <span className="text-sm font-medium text-yellow-900">
                          Atualizar contatos duplicados
                        </span>
                        <p className="text-xs text-yellow-700">
                          Os dados do CSV substituirão os dados existentes
                        </p>
                      </div>
                    </label>
                  </div>
                </div>
              )}

              {/* Invalid Rows */}
              {preview.invalid > 0 && (
                <details className="cursor-pointer">
                  <summary className="text-xs font-medium text-red-700 hover:text-red-800">
                    Ver linhas inválidas ({preview.invalid})
                  </summary>
                  <div className="mt-2 bg-red-50 border border-red-200 rounded p-2 max-h-24 overflow-y-auto">
                    {preview.invalidRows.map((inv, index) => (
                      <div key={index} className="text-xs text-red-600 py-1">
                        Linha {inv.row}: {inv.reason}
                      </div>
                    ))}
                  </div>
                </details>
              )}

              {/* No duplicates message */}
              {preview.duplicates === 0 && preview.newContacts > 0 && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                  <p className="text-sm text-green-800 font-medium">
                    Nenhum contato duplicado encontrado. {preview.newContacts} contato(s) novo(s) serão importados.
                  </p>
                </div>
              )}
            </>
          )}

          {/* ===== STEP 3: RESULT ===== */}
          {step === 'result' && importResult && (
            <>
              <div className={`border rounded-lg p-4 ${importResult.failedImports === 0 ? 'bg-green-50 border-green-200' : 'bg-yellow-50 border-yellow-200'}`}>
                <h3 className={`font-medium text-sm mb-3 ${importResult.failedImports === 0 ? 'text-green-900' : 'text-yellow-900'}`}>
                  {importResult.failedImports === 0 ? '✅' : '⚠️'} Resultado da Importação
                </h3>
                <div className={`grid ${importResult.updatedImports > 0 ? 'grid-cols-4' : 'grid-cols-3'} gap-2 mb-3`}>
                  <div className="bg-white rounded p-2 text-center">
                    <div className="text-lg font-bold text-gray-900">{importResult.totalRows}</div>
                    <div className="text-xs text-gray-600">Total</div>
                  </div>
                  <div className="bg-white rounded p-2 text-center">
                    <div className="text-lg font-bold text-green-600">{importResult.successfulImports}</div>
                    <div className="text-xs text-gray-600">Novos</div>
                  </div>
                  {importResult.updatedImports > 0 && (
                    <div className="bg-white rounded p-2 text-center">
                      <div className="text-lg font-bold text-blue-600">{importResult.updatedImports}</div>
                      <div className="text-xs text-gray-600">Atualizados</div>
                    </div>
                  )}
                  <div className="bg-white rounded p-2 text-center">
                    <div className="text-lg font-bold text-red-600">{importResult.failedImports + importResult.skippedDuplicates}</div>
                    <div className="text-xs text-gray-600">{importResult.skippedDuplicates > 0 ? 'Ignorados' : 'Erros'}</div>
                  </div>
                </div>

                {importResult.skippedDuplicates > 0 && (
                  <p className="text-xs text-yellow-700 mb-2">
                    {importResult.skippedDuplicates} contato(s) duplicado(s) foram ignorados (já existem no sistema).
                  </p>
                )}

                {(importResult.normalizedDuplicates || 0) > 0 && (
                  <p className="text-xs text-blue-700 mb-2">
                    {importResult.normalizedDuplicates} telefone(s) de contato(s) duplicado(s) foram normalizados.
                  </p>
                )}

                {importResult.errors.length > 0 && (
                  <details className="cursor-pointer">
                    <summary className="text-xs font-medium text-red-700 hover:text-red-800">Ver erros ({importResult.errors.length})</summary>
                    <div className="mt-2 bg-red-50 border border-red-200 rounded p-2 max-h-32 overflow-y-auto">
                      {importResult.errors.map((error, index) => (
                        <div key={index} className="text-xs text-red-600 py-1">{error}</div>
                      ))}
                    </div>
                  </details>
                )}
              </div>
            </>
          )}

          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4">
            {step === 'upload' && (
              <>
                <button
                  onClick={handleClose}
                  className="flex-1 bg-gray-100 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-200 font-medium transition-colors text-sm"
                >
                  Fechar
                </button>
                <button
                  onClick={handlePreview}
                  disabled={!file || isLoading}
                  className="btn-primary flex-1 py-2 px-4 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm"
                >
                  {isLoading ? (
                    <>
                      <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Analisando...
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                      </svg>
                      Analisar
                    </>
                  )}
                </button>
              </>
            )}

            {step === 'preview' && (
              <>
                <button
                  onClick={handleBack}
                  className="flex-1 bg-gray-100 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-200 font-medium transition-colors text-sm"
                >
                  Voltar
                </button>
                <button
                  onClick={handleImport}
                  disabled={isLoading || (preview?.newContacts === 0 && !updateExisting)}
                  className="btn-primary flex-1 py-2 px-4 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm"
                >
                  {isLoading ? (
                    <>
                      <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Importando...
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
                      </svg>
                      {updateExisting ? 'Importar e Atualizar' : 'Importar'}
                    </>
                  )}
                </button>
              </>
            )}

            {step === 'result' && (
              <button
                onClick={handleClose}
                className="flex-1 bg-gray-100 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-200 font-medium transition-colors text-sm"
              >
                Fechar
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
